import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ArrowRight, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { loanProfilingApi } from '@/services/api';
import { Field, Picker } from './OfferPieces';

const SOURCES = [
  { value: 'BANK_OFFER', label: 'Bank quoted' },
  { value: 'WE_ASKED', label: 'We asked the bank' },
  { value: 'CUSTOMER_COUNTER', label: 'Customer countered' },
  { value: 'CORRECTION', label: 'Correction' },
];
const SOURCE_STYLE = {
  BANK_OFFER: 'bg-blue-50 text-blue-700 border-blue-100',
  WE_ASKED: 'bg-amber-50 text-amber-700 border-amber-100',
  CUSTOMER_COUNTER: 'bg-violet-50 text-violet-700 border-violet-100',
  CORRECTION: 'bg-slate-100 text-slate-600 border-slate-200',
};

const Delta = ({ value, suffix = '%' }) => {
  if (value === null || value === undefined) return null;
  const up = value > 0;
  return (
    <span className={`text-[11px] font-semibold ${up ? 'text-rose-600' : 'text-emerald-600'}`}>
      {up ? '+' : ''}{value}{suffix}
    </span>
  );
};

/** The haggle, kept in order: what each side asked and what it cost the other. */
export const OfferNegotiation = ({ leadId, offer, onSaved, readOnly }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ source: 'BANK_OFFER', roi: '', tenure_months: '', commission_pct: '', note: '' });
  const rounds = offer.rounds || [];

  const log = async () => {
    if (!form.roi && !form.commission_pct && !form.tenure_months && !form.note.trim()) {
      toast.message('Add what changed, or a note about the conversation');
      return;
    }
    setBusy(true);
    try {
      const res = await loanProfilingApi.addOfferRound(leadId, offer.id, {
        source: form.source,
        roi: form.roi ? parseFloat(form.roi) : null,
        tenure_months: form.tenure_months ? parseInt(form.tenure_months, 10) : null,
        commission_pct: form.commission_pct ? parseFloat(form.commission_pct) : null,
        note: form.note.trim() || null,
        apply: form.source !== 'CUSTOMER_COUNTER',
      });
      onSaved(res.data);
      setForm({ source: 'BANK_OFFER', roi: '', tenure_months: '', commission_pct: '', note: '' });
      setOpen(false);
      toast.success('Negotiation logged');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not log that round');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`offer-negotiation-${offer.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="text-sm font-semibold text-slate-800">Negotiation</h4>
          <p className="text-[11px] text-slate-400">Every move on the rate, tenure and payout, in the order it happened.</p>
        </div>
        {!readOnly && (
          <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)} data-testid={`offer-round-toggle-${offer.id}`}>
            <MessageSquare className="w-3.5 h-3.5 mr-1.5" />{open ? 'Cancel' : 'Log a round'}
          </Button>
        )}
      </div>

      {open && !readOnly && (
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 mb-3 grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid={`offer-round-form-${offer.id}`}>
          <Field label="What happened">
            <Picker value={form.source} onChange={(v) => setForm((p) => ({ ...p, source: v }))} options={SOURCES}
              testId={`round-source-${offer.id}`} />
          </Field>
          <Field label="Rate (%)">
            <Input type="number" step="0.05" value={form.roi} onChange={(e) => setForm((p) => ({ ...p, roi: e.target.value }))}
              className="mt-1 h-9" placeholder="12.5" data-testid={`round-roi-${offer.id}`} />
          </Field>
          <Field label="Tenure (months)">
            <Input type="number" value={form.tenure_months} onChange={(e) => setForm((p) => ({ ...p, tenure_months: e.target.value }))}
              className="mt-1 h-9" placeholder="48" data-testid={`round-tenure-${offer.id}`} />
          </Field>
          <Field label="Payout (%)">
            <Input type="number" step="0.05" value={form.commission_pct} onChange={(e) => setForm((p) => ({ ...p, commission_pct: e.target.value }))}
              className="mt-1 h-9" placeholder="3.5" data-testid={`round-commission-${offer.id}`} />
          </Field>
          <Field label="Note" className="col-span-2 lg:col-span-1">
            <Input value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
              className="mt-1 h-9" placeholder="Customer has an ICICI quote" data-testid={`round-note-${offer.id}`} />
          </Field>
          <div className="col-span-2 lg:col-span-5 flex items-center justify-between">
            <p className="text-[11px] text-slate-400">
              A customer counter is recorded but does not change the live terms until the bank agrees.
            </p>
            <Button size="sm" onClick={log} disabled={busy} data-testid={`round-save-${offer.id}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}Save round
            </Button>
          </div>
        </div>
      )}

      {rounds.length === 0 ? (
        <p className="text-xs text-slate-400" data-testid={`offer-rounds-empty-${offer.id}`}>No rounds logged yet.</p>
      ) : (
        <ol className="relative border-l border-slate-200 ml-1.5 space-y-3" data-testid={`offer-rounds-${offer.id}`}>
          {rounds.map((r, i) => (
            <li key={r.id || i} className="ml-4" data-testid={`offer-round-${offer.id}-${i}`}>
              <span className="absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full bg-slate-300" />
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${SOURCE_STYLE[r.source] || SOURCE_STYLE.CORRECTION}`}>
                  {(SOURCES.find((s) => s.value === r.source) || {}).label || r.source}
                </span>
                {r.roi != null && (
                  <span className="text-sm text-slate-800 font-medium tabular-nums">{r.roi}%</span>
                )}
                <Delta value={r.roi_delta} />
                {r.tenure_months != null && <span className="text-xs text-slate-500">{r.tenure_months} months</span>}
                {r.commission_pct != null && (
                  <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                    payout {r.commission_pct}% <Delta value={r.commission_delta} />
                  </span>
                )}
                <span className="text-[11px] text-slate-400 ml-auto">
                  {r.at ? new Date(r.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''} · {r.by}
                </span>
              </div>
              {r.note && <p className="text-xs text-slate-500 mt-0.5">{r.note}</p>}
            </li>
          ))}
        </ol>
      )}

      {rounds.length > 1 && offer.roi && (
        <div className="mt-3 text-xs text-slate-500 inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5"
          data-testid={`offer-negotiation-summary-${offer.id}`}>
          <span>Opened at {rounds[0].roi ?? '—'}%</span>
          <ArrowRight className="w-3.5 h-3.5 text-slate-300" />
          <span className="font-semibold text-slate-800">now {offer.roi}% for {offer.tenure_months} months</span>
          {offer.commission_pct != null && <span>· payout {offer.commission_pct}%</span>}
        </div>
      )}
    </div>
  );
};
